/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { ActivityObject } from '../../params/redux/activity';
import AddNoteModal from './modal/notes/add_note';
import AddReminderModal from './modal/reminder/add_reminder';
import CardWrapper from '../wrapper/card';
import CustomDropdown from '../reusable/dropdown';
import { DealFileObject } from '../../params/redux/deal';
import DeleteDealDocumentModal from './modal/delete_doc';
import DeleteNoteModal from './modal/notes/delete_note';
import DeleteReminderModal from './modal/reminder/delete_reminder';
import EditDocumentTitleModal from './modal/edit_doc';
import { fetchAllDealFilesThunk } from '../../redux/thunks/deal';
import { fetchDealActivityThunk } from '../../redux/thunks/activity';
import { getDealsNotesThunk } from '../../redux/thunks/note';
import { getDealsRemindersThunk } from '../../redux/thunks/reminder';
import { settingSelectedNote } from '../../redux/slices/note';
import { settingSelectedReminder } from '../../redux/slices/reminder';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, CircularProgress, Divider, IconButton, Typography } from '@mui/material';
import { badgeColors, isHTML, timelineOptions } from '../../helper';
import { DealsNoteObject, DealsReminderObject } from '../../params/redux/pipeline';
import { DeleteOutlineOutlined, DownloadOutlined, EditOutlined, ListAltOutlined, VerifiedUser, VisibilityOutlined } from '@mui/icons-material';
import { settingSelectedDealFile, settingSelectedEmailAndSMSTab } from '../../redux/slices/deal';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

type TimelineType = 'document' | 'note' | 'reminder' | 'general' | 'email' | 'sms' | 'calls' | 'contacts';

const ActivityTimeline = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedTimeline, setSelectedTimeline] = useState<TimelineType>('general');
    const [isLoading, setIsLoading] = useState<number | null>(null);
    const [noteModal, setNoteModal] = useState<boolean>(false);
    const [noteDeleteModal, setNoteDeleteModal] = useState<boolean>(false);
    const [reminderModal, setReminderModal] = useState<boolean>(false);
    const [reminderDeleteModal, setReminderDeleteModal] = useState<boolean>(false);
    const [docModal, setDocModal] = useState<boolean>(false);
    const [docDeleteModal, setDocDeleteModal] = useState<boolean>(false);
    const { selectedDealDetail } = useSelector((state: RootState) => state.deal);
    const { activities, loaders } = useSelector((state: RootState) => state.activity);

    const filteredActivities = selectedTimeline === 'general' ? activities : activities.filter((activity) => activity.type === selectedTimeline);

    useEffect(() => {
        const fetchActivities = async () => {
            try {
                await dispatch(fetchDealActivityThunk()).unwrap();
            } catch (e) {
                // Catch errors
            }
        };
        fetchActivities();
    }, []);

    const noteHandler = async (id: number, type: 'edit' | 'delete', note_id: number) => {
        try {
            setIsLoading(id);
            const response: DealsNoteObject[] = await dispatch(getDealsNotesThunk(selectedDealDetail?.id as number)).unwrap() as DealsNoteObject[];
            const selectedNote = response.find((n) => n.id === note_id);
            if (selectedNote) {
                dispatch(settingSelectedNote(selectedNote));
                if (type === 'edit') {
                    setNoteModal(true);
                    setNoteDeleteModal(false);
                } else {
                    setNoteDeleteModal(true);
                    setNoteModal(false);
                }
            }
        } catch (e) {
            //
        } finally {
            setIsLoading(null);
        }
    };

    const reminderHandler = async (id: number, type: 'edit' | 'delete', reminder_id: number) => {
        try {
            setIsLoading(id);
            const response: DealsReminderObject[] = await dispatch(getDealsRemindersThunk(selectedDealDetail?.id as number)).unwrap() as DealsReminderObject[];
            const selectedReminder = response.find((n) => n.id === reminder_id);
            if (selectedReminder) {
                dispatch(settingSelectedReminder(selectedReminder));
                if (type === 'edit') {
                    setReminderModal(true);
                    setReminderDeleteModal(false);
                } else {
                    setReminderDeleteModal(true);
                    setReminderModal(false);
                }
            }
        } catch (e) {
            //
        } finally {
            setIsLoading(null);
        }
    };

    const fileHandler = async (id: number, type: 'edit' | 'delete' | 'view' | 'download', fileId: number, activity: ActivityObject) => {
        try {
            setIsLoading(id);
            if (type === 'view') {
                window.open(`${process.env.REACT_APP_FILE_PATH}${activity?.message}`, '_blank');
            } else if (type === 'download') {
                const file_id = activity?.message?.split('/')[1];
                if (!file_id) return;

                const link = document.createElement('a');
                link.href = `${process.env.REACT_APP_API_BASE_URL}download-file/${file_id}`;
                link.setAttribute('download', '');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                // window.open(`${process.env.REACT_APP_API_BASE_URL}download-file/${activity?.message?.split('/')[1] as string}`, '_blank');
            } else {
                const response: DealFileObject[] = await dispatch(fetchAllDealFilesThunk()).unwrap() as DealFileObject[];
                const selectedFile = response.find((n) => n.id === activity.item_id);
                if (selectedFile) {
                    dispatch(settingSelectedDealFile(selectedFile));
                    if (type === 'edit') {
                        setDocModal(true);
                        setDocDeleteModal(false);
                    } else if (type === 'delete') {
                        setDocDeleteModal(true);
                        setDocModal(false);
                    }
                }
            }
        } catch (e) {
            //
        } finally {
            setIsLoading(null);
        }
    };

    return (
        <CardWrapper>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap="10px">
                    <ListAltOutlined color="disabled" />
                    <Typography variant="h6" color="textSecondary">
                        Activity Timeline
                    </Typography>
                </Box>
                <Box display="flex" gap="10px" mb={1}>
                    <CustomDropdown
                        options={timelineOptions}
                        value={selectedTimeline}
                        onChange={(e) => setSelectedTimeline(e.target.value as TimelineType)}
                        sx={{
                            marginBottom: '0'
                        }}
                    />
                    <Button variant="outlined" color="primary" fullWidth onClick={() => dispatch(settingSelectedEmailAndSMSTab(true))}>
                        Communication
                    </Button>
                </Box>
            </Box>
            <Divider />
            <Box mt={2} maxHeight="60vh" overflow="auto">
                {loaders.isLoading
                    ? <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" my={2}>
                        <CircularProgress size={24} />
                        <Typography ml={2}>Please wait...</Typography>
                    </Box>
                    : filteredActivities.map((activity) => (
                        <Box key={activity.id} mb={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                {/* Icon */}
                                <Box display="flex" alignItems="center">
                                    <Avatar
                                        sx={{
                                            bgcolor: badgeColors[activity.badge_color],
                                            color: '#FFF',
                                            width: 40,
                                            height: 40,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginRight: 2
                                        }}
                                    >
                                        <VerifiedUser sx={{ color: 'white' }} />
                                    </Avatar>

                                    {/* Content */}
                                    <Box>
                                        <Typography variant="body1" fontWeight="bold">
                                            {activity.description}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {activity.type === 'reminder'
                                                ? activity.message?.split('<br>')[0]
                                                : activity.type === 'calls'
                                                    ? activity.message
                                                    : new Date(activity.created_at).toLocaleString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                        </Typography>
                                    </Box>
                                </Box>
                                {
                                    activity.type === 'calls' && activity.call_recording && <a style={{ padding: '0 10px' }} href={`${process.env.REACT_APP_FILE_PATH}${activity.call_recording}`} target="_blank" rel="noreferrer">View Recording</a>
                                }
                                {isLoading !== activity.id && (activity.type === 'note' || activity.type === 'document' || activity.type === 'reminder') && (activity.status === 'active') && (activity.action_type === 'add') && <Box>
                                    {activity.type === 'document' && <IconButton
                                        color="primary"
                                        onClick={() => (fileHandler(activity.id, 'view', activity.item_id as number, activity))}
                                    >
                                        <VisibilityOutlined />
                                    </IconButton>}
                                    {activity.type === 'document' && <IconButton
                                        color="primary"
                                        onClick={() => (fileHandler(activity.id, 'download', activity.item_id as number, activity))}
                                    >
                                        <DownloadOutlined />
                                    </IconButton>}
                                    <IconButton
                                        color="primary"
                                        onClick={() => (
                                            activity.type === 'note'
                                                ? noteHandler(activity.id, 'edit', activity.item_id as number)
                                                : activity.type === 'reminder'
                                                    ? reminderHandler(activity.id, 'edit', activity.item_id as number)
                                                    : activity.type === 'document'
                                                        ? fileHandler(activity.id, 'edit', activity.item_id as number, activity)
                                                        : null)}
                                    >
                                        <EditOutlined />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => (
                                            activity.type === 'note'
                                                ? noteHandler(activity.id, 'delete', activity.item_id as number)
                                                : activity.type === 'reminder'
                                                    ? reminderHandler(activity.id, 'delete', activity.item_id as number)
                                                    : activity.type === 'document'
                                                        ? fileHandler(activity.id, 'delete', activity.item_id as number, activity)
                                                        : null)}
                                    >
                                        <DeleteOutlineOutlined />
                                    </IconButton>
                                </Box>}
                                {
                                    isLoading && isLoading === activity.id && <Box>
                                        <CircularProgress color="primary" />
                                    </Box>
                                }
                            </Box>
                            {activity.type === 'note' && isHTML(activity?.message as string)
                                ? <Box dangerouslySetInnerHTML={{ __html: activity?.message as string }}></Box>
                                : activity.type === 'note'
                                    ? <Typography variant="body2" color="rgba(111,118,126,1)" fontSize="12px" fontWeight="400">
                                        {activity?.message as string}
                                    </Typography>
                                    : null
                            }
                            {
                                activity.type === 'reminder' && <Box my={1}>
                                    <Typography variant="body2" color="textSecondary">{activity.message?.split('<br>')[1]}</Typography>
                                </Box>
                            }
                        </Box>
                    ))}
            </Box>
            {noteModal && <AddNoteModal onClose={() => setNoteModal(false)} open={noteModal} />}
            {noteDeleteModal && <DeleteNoteModal open={noteDeleteModal} onClose={() => setNoteDeleteModal(false)} />}
            {reminderModal && <AddReminderModal onClose={() => setReminderModal(false)} open={reminderModal} />}
            {reminderDeleteModal && <DeleteReminderModal onClose={() => setReminderDeleteModal(false)} open={reminderDeleteModal} />}
            {docModal && <EditDocumentTitleModal open={docModal} onClose={() => setDocModal(false)} />}
            {docDeleteModal && <DeleteDealDocumentModal open={docDeleteModal} onClose={() => setDocDeleteModal(false)} />}
        </CardWrapper>
    );
};

export default ActivityTimeline;